import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
  Alert,
  IconButton,
  Typography,
  SelectChangeEvent
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Clear as ClearIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { getProducts, addProduct, editProduct, deleteProduct, getProductById, ProductDto } from '../../services/expensesApi';
import ProductForm from './ProductForm';
import ProductDetail from './ProductDetail';

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ProductDto | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailProduct, setDetailProduct] = useState<ProductDto | null>(null);

  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<keyof ProductDto>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      setProducts(await getProducts());
    } catch (e: any) {
      setError(e.message || 'Não foi possível carregar os produtos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  // Filtered and sorted data
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.code && product.code.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply quantity filter (instead of status since there's no isActive)
    if (statusFilter === 'in-stock') {
      filtered = filtered.filter(product => product.quantity > 0);
    } else if (statusFilter === 'out-of-stock') {
      filtered = filtered.filter(product => product.quantity === 0);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortBy as keyof ProductDto] || '';
      const bValue = b[sortBy as keyof ProductDto] || '';
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const comparison = Number(bValue) - Number(aValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      return 0;
    });

    return filtered;
  }, [products, searchTerm, statusFilter, sortBy, sortOrder]);

  // Paginated data
  const paginatedProducts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedProducts.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedProducts, page, rowsPerPage]);

  // Total count for pagination
  const totalProducts = filteredAndSortedProducts.length;

  const handleSort = (property: keyof ProductDto) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPage(0);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };
  const handleEdit = async (publicId: string) => {
    setLoading(true);
    try {
      const prod = await getProductById(publicId);
      setEditing(prod);
      setShowForm(true);
    } finally { setLoading(false); }
  };
  const handleDelete = async (publicId: string) => {
    if (!window.confirm('Eliminar este produto?')) return;
    setLoading(true);
    try {
      await deleteProduct(publicId);
      fetchProducts();
    } finally { setLoading(false); }
  };
  const handleDetails = async (publicId: string) => {
    setLoading(true);
    try {
      setDetailProduct(await getProductById(publicId));
      setShowDetail(true);
    } finally { setLoading(false); }
  };
  const handleFormSubmit = async (data: Partial<ProductDto>) => {
    setLoading(true);
    try {
      if (editing && editing.publicId) {
        await editProduct({ ...editing, ...data });
      } else {
        await addProduct(data);
      }
      setShowForm(false);
      fetchProducts();
    } finally { setLoading(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={loading}
        >
          Adicionar produto
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtering Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
            <TextField
              fullWidth
              size="small"
              label="Pesquisar produtos..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado do stock</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                label="Estado do stock"
              >
                <MenuItem value="">Todos os produtos</MenuItem>
                <MenuItem value="in-stock">Em stock</MenuItem>
                <MenuItem value="out-of-stock">Sem stock</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            disabled={!searchTerm && !statusFilter}
          >
            Limpar
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            A mostrar {paginatedProducts.length} de {filteredAndSortedProducts.length} produtos
          </Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'code'}
                  direction={sortBy === 'code' ? sortOrder : 'asc'}
                  onClick={() => handleSort('code')}
                >
                  Código
                </TableSortLabel>
              </TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'quantity'}
                  direction={sortBy === 'quantity' ? sortOrder : 'asc'}
                  onClick={() => handleSort('quantity')}
                >
                  Quantidade
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.map((product) => (
              <TableRow key={product.publicId}>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.code || '-'}</TableCell>
                <TableCell>{product.description || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {product.quantity}
                    {product.quantity === 0 && (
                      <Typography variant="caption" color="error">
                        Sem stock
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleDetails(product.publicId)}
                    disabled={loading}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(product.publicId)}
                    disabled={loading}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(product.publicId)}
                    disabled={loading}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedProducts.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    Nenhum produto encontrado. Adicione o primeiro produto para começar.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <TablePagination
          component="div"
          count={totalProducts}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>

      <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar produto' : 'Adicionar produto'}</DialogTitle>
        <DialogContent>
          <ProductForm initial={editing || {}} onSubmit={handleFormSubmit} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={showDetail} onClose={() => setShowDetail(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do produto</DialogTitle>
        <DialogContent>
          {detailProduct && <ProductDetail product={detailProduct} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetail(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductList;
